import React from 'react';
import { Switch, NumberInput } from '@/components/ui';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import {
  TIMELINE_AXIS_LABEL_CLASS,
  TIMELINE_AXIS_TICK_CLASS,
  TIMELINE_AXIS_UNIT_CLASS,
  TIMELINE_NOTE_TEXT_CLASS,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { HATCH_BG } from '@/components/config_forms/shared/help-graphics/timeline/TimelinePrimitives';
import { ControlRow } from '@/components/config_forms/shared/ControlRow';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface BreakCountTimelineProps {
  requiredBreaks: number;
  scalingEnabled: boolean;
  thresholdHours: number | null;
  additionalBreakIntervalHours: number | null;
  thresholdFromBilling: number | null;
  onRequiredBreaksChange?: (value: number | null) => void;
  onScalingToggle?: (enabled: boolean) => void;
  onThresholdChange?: (value: number | null) => void;
  onAdditionalBreakIntervalChange?: (value: number | null) => void;
}

/* ------------------------------------------------------------------ */
/*  Hatch pattern (inline style — scenario-green is theme-registered   */
/*  but hatching requires repeating-linear-gradient in style attr)     */
/* ------------------------------------------------------------------ */

const COUNT_HATCH_BG = HATCH_BG('color-mix(in srgb, var(--primary) 10%, transparent)');

const COUNT_AXIS_TICK_STYLE = {
  color: 'var(--primary-text)',
} as const;

/* ------------------------------------------------------------------ */
/*  SegmentedBar                                                       */
/* ------------------------------------------------------------------ */

const SegmentedBar: React.FC<{
  requiredBreaks: number;
  thresholdHours: number;
  intervalHours: number;
}> = ({ requiredBreaks, thresholdHours, intervalHours }) => {
  const workedFraction = 0.6;
  const workedPartial = intervalHours * workedFraction;
  const unworkedPartial = intervalHours * (1 - workedFraction);
  const continuationFlex = intervalHours * 0.6;

  const total = thresholdHours + intervalHours + intervalHours + continuationFlex;
  const shiftEndPct = ((thresholdHours + intervalHours + workedPartial) / total) * 100;

  const thresholdPct = (thresholdHours / total) * 100;
  const interval1Pct = ((thresholdHours + intervalHours) / total) * 100;
  const interval2Pct = ((thresholdHours + intervalHours + intervalHours) / total) * 100;

  return (
    /* bar-area: single positioning context for label + tick + bar */
    <div className="relative">
      {/* Segmented bar — inset box-shadow for border/bevel (no layout impact) */}
      <div
        className="relative flex items-stretch h-14 rounded-[0.25rem] overflow-hidden"
        style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 40%)' }}
      >
        {/* Base threshold — green */}
        <div
          className="flex items-center justify-center self-stretch bg-scenario-green/8 motion-safe:transition-[flex-grow] motion-safe:duration-300"
          style={{ flex: thresholdHours }}
        >
          <span className="font-label text-[10px] sm:text-[12px] leading-none tracking-[0.3px] text-scenario-green/85 whitespace-nowrap overflow-hidden text-ellipsis px-2">
            {requiredBreaks} breaks required
          </span>
        </div>

        {/* Complete interval — solid amber */}
        <div
          className="flex items-center justify-center self-stretch bg-primary/[0.06] motion-safe:transition-[flex-grow] motion-safe:duration-300"
          style={{ flex: intervalHours }}
        >
          <span className="font-label text-[10px] sm:text-[12px] leading-none tracking-[0.3px] text-primary-text/85 whitespace-nowrap overflow-hidden text-ellipsis px-2">
            +1 break
          </span>
        </div>

        {/* Incomplete interval — worked portion (solid amber, before Shift End) */}
        <div
          className="self-stretch bg-primary/[0.06] motion-safe:transition-[flex-grow] motion-safe:duration-300"
          style={{ flex: workedPartial }}
        />

        {/* Incomplete interval — unworked portion (hatched, after Shift End) */}
        <div
          className="self-stretch motion-safe:transition-[flex-grow] motion-safe:duration-300"
          style={{ flex: unworkedPartial, background: COUNT_HATCH_BG }}
        />

        {/* Continuation — hatched ellipsis */}
        <div
          className="flex items-center justify-center self-stretch"
          style={{ flex: continuationFlex, background: COUNT_HATCH_BG }}
        >
          <span className="font-mono text-[14px] leading-none text-muted-foreground/25 tracking-[2px]">···</span>
        </div>

        {/* Segment dividers — same coordinate space as flex content */}
        <div className="absolute top-0 bottom-0 w-[2px] z-[1]" style={{ left: `${thresholdPct}%`, transform: 'translateX(-50%)', background: 'color-mix(in srgb, var(--color-scenario-green), transparent 70%)' }} />
        <div className="absolute top-0 bottom-0 w-px z-[1]" style={{ left: `${interval1Pct}%`, transform: 'translateX(-50%)', borderRight: '1px dashed color-mix(in srgb, var(--primary), transparent 75%)' }} />
        <div className="absolute top-0 bottom-0 w-px z-[1]" style={{ left: `${interval2Pct}%`, transform: 'translateX(-50%)', borderRight: '1px dashed color-mix(in srgb, var(--primary), transparent 75%)' }} />
        <div className="absolute top-0 bottom-0 w-[2px] bg-muted-foreground/50 z-[2]" style={{ left: `${shiftEndPct}%`, transform: 'translateX(-50%)' }} />
      </div>

      {/* Bottom axis labels */}
      <div className="relative h-[22px] mt-1">
        {/* Start and End labels below timeline */}
        <span className={`absolute top-1 left-0 whitespace-nowrap ${TIMELINE_AXIS_LABEL_CLASS}`}>
          Start
        </span>
        <span
          className={`absolute top-1 whitespace-nowrap ${TIMELINE_AXIS_LABEL_CLASS}`}
          style={{ left: `${shiftEndPct}%`, transform: 'translateX(-50%)' }}
        >
          End
        </span>

        {/* Threshold tick + label */}
        <div
          className="absolute -top-1 w-px h-1 bg-muted-foreground/40"
          style={{ left: `${thresholdPct}%`, transform: 'translateX(-50%)' }}
        />
        <div
          className="absolute top-1 whitespace-nowrap"
          style={{ left: `${thresholdPct}%`, transform: 'translateX(-50%)' }}
        >
          <span className={`${TIMELINE_AXIS_TICK_CLASS} font-normal`} style={COUNT_AXIS_TICK_STYLE}>{thresholdHours}</span>
          <span className={`${TIMELINE_AXIS_UNIT_CLASS} font-normal`} style={COUNT_AXIS_TICK_STYLE}>h</span>
        </div>

        {/* First interval tick + label */}
        <div
          className="absolute -top-1 w-px h-1 bg-muted-foreground/40"
          style={{ left: `${interval1Pct}%`, transform: 'translateX(-50%)' }}
        />
        <div
          className="absolute top-1 whitespace-nowrap"
          style={{ left: `${interval1Pct}%`, transform: 'translateX(-50%)' }}
        >
          <span className={`${TIMELINE_AXIS_TICK_CLASS} font-normal`} style={COUNT_AXIS_TICK_STYLE}>+{intervalHours}</span>
          <span className={`${TIMELINE_AXIS_UNIT_CLASS} font-normal`} style={COUNT_AXIS_TICK_STYLE}>h</span>
        </div>

        {/* Second interval tick + label */}
        <div
          className="absolute -top-1 w-px h-1 bg-muted-foreground/40"
          style={{ left: `${interval2Pct}%`, transform: 'translateX(-50%)' }}
        />
        <div
          className="absolute top-1 whitespace-nowrap"
          style={{ left: `${interval2Pct}%`, transform: 'translateX(-50%)' }}
        >
          <span className={`${TIMELINE_AXIS_TICK_CLASS} font-normal`} style={COUNT_AXIS_TICK_STYLE}>+{intervalHours}</span>
          <span className={`${TIMELINE_AXIS_UNIT_CLASS} font-normal`} style={COUNT_AXIS_TICK_STYLE}>h</span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  FlatBar (scaling disabled)                                         */
/* ------------------------------------------------------------------ */

const FlatBar: React.FC<{ requiredBreaks: number }> = ({ requiredBreaks }) => (
  <div>
    {/* Single green bar */}
    <div
      className="flex items-stretch h-14 rounded-[0.25rem] overflow-hidden"
      style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 40%)' }}
    >
      <div className="flex-1 flex items-center justify-center bg-scenario-green/8">
        <span className="font-label text-[10px] sm:text-[12px] leading-none tracking-[0.3px] text-scenario-green/85">
          {requiredBreaks} breaks required
        </span>
      </div>
    </div>

    {/* Axis labels below timeline */}
    <div className="flex justify-between items-start mt-1">
      <span className={TIMELINE_AXIS_LABEL_CLASS}>
        Start
      </span>
      <span className={TIMELINE_AXIS_LABEL_CLASS}>
        End
      </span>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  SurchargeNote                                                      */
/* ------------------------------------------------------------------ */

const SurchargeNote: React.FC = () => (
  <div className={`mt-1 mb-3 px-2 py-2 rounded-[0.25rem] border border-destructive/10 bg-destructive/[0.06] ${TIMELINE_NOTE_TEXT_CLASS}`}>
    <strong className="text-destructive-text/85 font-medium">Surcharge:</strong>{' '}
    Each missing break triggers your configured surcharge.
  </div>
);

/* ------------------------------------------------------------------ */
/*  BreakCountTimeline (exported)                                      */
/* ------------------------------------------------------------------ */

export const BreakCountTimeline: React.FC<BreakCountTimelineProps> = ({
  requiredBreaks,
  scalingEnabled,
  thresholdHours,
  additionalBreakIntervalHours,
  thresholdFromBilling,
  onRequiredBreaksChange,
  onScalingToggle,
  onThresholdChange,
  onAdditionalBreakIntervalChange,
}) => {
  const effectiveThreshold = thresholdFromBilling ?? thresholdHours ?? 10;
  const effectiveInterval = additionalBreakIntervalHours ?? 4;
  const showThresholdControl = thresholdFromBilling == null;

  return (
    <div role="group" aria-label="Break count timeline">
      {/* Flat bar — always rendered when scaling disabled */}
      <AnimatedCollapse open={!scalingEnabled} duration={300}>
        <FlatBar requiredBreaks={requiredBreaks} />
      </AnimatedCollapse>

      {/* Segmented bar — rendered when scaling enabled */}
      <AnimatedCollapse open={scalingEnabled} duration={300}>
        <SegmentedBar
          requiredBreaks={requiredBreaks}
          thresholdHours={effectiveThreshold}
          intervalHours={effectiveInterval}
        />
      </AnimatedCollapse>

      {/* Surcharge note */}
      <SurchargeNote />

      {/* Controls — outside the bordered panel for full width */}
      <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
        {/* Required Breaks */}
        {onRequiredBreaksChange && (
          <ControlRow
            label="Meal Breaks Required"
            description={scalingEnabled
              ? 'Qualifying meal breaks required within the threshold.'
              : 'Qualifying meal breaks required per shift.'}
          >
            <NumberInput
              aria-label="Meal breaks required"
              size="xs"
              touchTarget
              value={requiredBreaks || ''}
              onChange={(e) => {
                const v = e.target.value;
                onRequiredBreaksChange(v === '' ? null : Math.max(1, parseInt(v, 10) || 0));
              }}
              minValue={1}
              maxValue={10}
              step={1}
            />
          </ControlRow>
        )}

        {/* Scale with Shift Length */}
        {onScalingToggle && (
          <ControlRow label="Scale with Shift Length" description="Require more breaks as shift length increases.">
            <Switch
              checked={scalingEnabled}
              onCheckedChange={onScalingToggle}
              aria-label="Toggle break scaling"
            />
          </ControlRow>
        )}

        {/* Threshold + Interval — visible when scaling is enabled */}
        <AnimatedCollapse open={scalingEnabled && (!!onThresholdChange || !!onAdditionalBreakIntervalChange)} duration={300}>
          <div className="divide-y divide-border/20">
            {/* Break Threshold — hidden when auto-derived from billing */}
            {onThresholdChange && showThresholdControl && (
              <ControlRow label="Break Threshold" description="Shifts at or under this length require the breaks above.">
                <NumberInput
                  aria-label="Break threshold"
                  unit="hrs"
                  size="xs"
                  touchTarget
                  value={thresholdHours ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdChange(v === '' ? null : Math.max(1, parseFloat(v) || 0));
                  }}
                  minValue={1}
                  maxValue={24}
                  step={0.5}
                />
              </ControlRow>
            )}

            {/* Additional Break Every */}
            {onAdditionalBreakIntervalChange && (
              <ControlRow label="Additional Break Every" description="Beyond the threshold, one more break per this interval.">
                <NumberInput
                  aria-label="Additional break every"
                  unit="hrs"
                  size="xs"
                  touchTarget
                  value={additionalBreakIntervalHours ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    onAdditionalBreakIntervalChange(v === '' ? null : Math.max(1, parseFloat(v) || 0));
                  }}
                  minValue={1}
                  maxValue={12}
                  step={0.5}
                />
              </ControlRow>
            )}
          </div>
        </AnimatedCollapse>
      </div>
    </div>
  );
};
